import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

export interface AdminThemeColors {
  background: string;
  foreground: string;
  card: string;
  cardForeground: string;
  primary: string;
  primaryForeground: string;
  muted: string;
  mutedForeground: string;
  border: string;
  accent: string;
  accentForeground: string;
  /** Cor de fundo do menu lateral do admin (opcional). */
  menuBackground?: string;
  /** Cor dos ícones/textos do menu lateral (opcional). */
  menuForeground?: string;
  /** Cor do item selecionado / grupo ativo do menu (opcional). */
  menuActive?: string;
}

export const DEFAULT_MENU_COLORS = {
  menuBackground: "220 10% 13%",
  menuForeground: "40 20% 97%",
  menuActive: "15 50% 45%",
} as const;

export interface AdminTheme {
  id: string;
  name: string;
  displayFont: string;
  bodyFont: string;
  colors: AdminThemeColors;
  isDark: boolean;
}

export const availableFonts = [
  { id: "inter", name: "Inter", value: "'Inter', system-ui, sans-serif" },
  { id: "dm-sans", name: "DM Sans", value: "'DM Sans', system-ui, sans-serif" },
  { id: "plus-jakarta", name: "Plus Jakarta Sans", value: "'Plus Jakarta Sans', system-ui, sans-serif" },
  { id: "space-grotesk", name: "Space Grotesk", value: "'Space Grotesk', system-ui, sans-serif" },
  { id: "outfit", name: "Outfit", value: "'Outfit', system-ui, sans-serif" },
  { id: "nunito-sans", name: "Nunito Sans", value: "'Nunito Sans', system-ui, sans-serif" },
  { id: "manrope", name: "Manrope", value: "'Manrope', system-ui, sans-serif" },
  { id: "poppins", name: "Poppins", value: "'Poppins', system-ui, sans-serif" },
  { id: "roboto", name: "Roboto", value: "'Roboto', system-ui, sans-serif" },
  { id: "montserrat", name: "Montserrat", value: "'Montserrat', system-ui, sans-serif" },
  { id: "playfair", name: "Playfair Display", value: "'Playfair Display', serif" },
  { id: "lora", name: "Lora", value: "'Lora', serif" },
  { id: "site-default", name: "Padrão do Site", value: "var(--font-display)" },
];

export const adminThemes: AdminTheme[] = [
  {
    id: "site-default",
    name: "Tema Atual (Loja)",
    // Uses the existing site/admin default fonts (do not override)
    displayFont: "var(--font-display)",
    bodyFont: "var(--font-body)",
    isDark: false,
    // Mirrors current :root tokens from index.css so the preview matches
    colors: {
      background: "40 20% 97%",
      foreground: "220 10% 15%",
      card: "45 40% 96%",
      cardForeground: "220 10% 15%",
      primary: "15 50% 55%",
      primaryForeground: "40 20% 97%",
      muted: "30 20% 92%",
      mutedForeground: "220 8% 45%",
      border: "30 20% 88%",
      accent: "45 40% 90%",
      accentForeground: "220 10% 15%",
      menuBackground: "220 10% 13%",
      menuForeground: "40 20% 97%",
      menuActive: "15 50% 45%",
    },
  },
  {
    id: "slate-modern",
    name: "Slate Modern",
    displayFont: "'Inter', system-ui, sans-serif",
    bodyFont: "'Inter', system-ui, sans-serif",
    isDark: false,
    colors: {
      background: "210 20% 98%",
      foreground: "222 47% 11%",
      card: "0 0% 100%",
      cardForeground: "222 47% 11%",
      primary: "222 47% 20%",
      primaryForeground: "210 20% 98%",
      muted: "210 20% 94%",
      mutedForeground: "215 16% 47%",
      border: "214 20% 90%",
      accent: "210 20% 94%",
      accentForeground: "222 47% 11%",
      menuBackground: "222 47% 13%",
      menuForeground: "210 20% 96%",
      menuActive: "222 47% 28%",
    },
  },
  {
    id: "midnight-dark",
    name: "Midnight Dark",
    displayFont: "'Inter', system-ui, sans-serif",
    bodyFont: "'Inter', system-ui, sans-serif",
    isDark: true,
    colors: {
      background: "222 47% 8%",
      foreground: "210 20% 95%",
      card: "222 47% 11%",
      cardForeground: "210 20% 95%",
      primary: "210 100% 60%",
      primaryForeground: "222 47% 8%",
      muted: "217 33% 17%",
      mutedForeground: "215 20% 65%",
      border: "217 33% 20%",
      accent: "217 33% 17%",
      accentForeground: "210 20% 95%",
      menuBackground: "222 47% 6%",
      menuForeground: "210 20% 92%",
      menuActive: "210 100% 40%",
    },
  },
  {
    id: "emerald-fresh",
    name: "Emerald Fresh",
    displayFont: "'DM Sans', system-ui, sans-serif",
    bodyFont: "'DM Sans', system-ui, sans-serif",
    isDark: false,
    colors: {
      background: "150 20% 97%",
      foreground: "160 25% 10%",
      card: "0 0% 100%",
      cardForeground: "160 25% 10%",
      primary: "158 64% 40%",
      primaryForeground: "150 20% 97%",
      muted: "150 15% 92%",
      mutedForeground: "160 10% 45%",
      border: "150 15% 88%",
      accent: "150 15% 92%",
      accentForeground: "160 25% 10%",
      menuBackground: "160 25% 12%",
      menuForeground: "150 20% 96%",
      menuActive: "158 64% 32%",
    },
  },
  {
    id: "violet-elegant",
    name: "Violet Elegant",
    displayFont: "'Plus Jakarta Sans', system-ui, sans-serif",
    bodyFont: "'Plus Jakarta Sans', system-ui, sans-serif",
    isDark: false,
    colors: {
      background: "270 25% 98%",
      foreground: "270 25% 12%",
      card: "0 0% 100%",
      cardForeground: "270 25% 12%",
      primary: "262 83% 58%",
      primaryForeground: "270 25% 98%",
      muted: "270 15% 93%",
      mutedForeground: "270 10% 50%",
      border: "270 15% 88%",
      accent: "270 15% 93%",
      accentForeground: "270 25% 12%",
      menuBackground: "270 25% 14%",
      menuForeground: "270 25% 96%",
      menuActive: "262 60% 45%",
    },
  },
  {
    id: "carbon-minimal",
    name: "Carbon Minimal",
    displayFont: "'Space Grotesk', system-ui, sans-serif",
    bodyFont: "'Space Grotesk', system-ui, sans-serif",
    isDark: true,
    colors: {
      background: "0 0% 5%",
      foreground: "0 0% 95%",
      card: "0 0% 9%",
      cardForeground: "0 0% 95%",
      primary: "0 0% 100%",
      primaryForeground: "0 0% 5%",
      muted: "0 0% 15%",
      mutedForeground: "0 0% 60%",
      border: "0 0% 18%",
      accent: "0 0% 15%",
      accentForeground: "0 0% 95%",
      menuBackground: "0 0% 4%",
      menuForeground: "0 0% 92%",
      menuActive: "0 0% 22%",
    },
  },
  {
    id: "ocean-calm",
    name: "Ocean Calm",
    displayFont: "'Outfit', system-ui, sans-serif",
    bodyFont: "'Outfit', system-ui, sans-serif",
    isDark: false,
    colors: {
      background: "200 25% 97%",
      foreground: "200 25% 10%",
      card: "0 0% 100%",
      cardForeground: "200 25% 10%",
      primary: "200 75% 45%",
      primaryForeground: "200 25% 97%",
      muted: "200 20% 92%",
      mutedForeground: "200 15% 45%",
      border: "200 20% 88%",
      accent: "200 20% 92%",
      accentForeground: "200 25% 10%",
      menuBackground: "205 40% 14%",
      menuForeground: "200 25% 96%",
      menuActive: "200 75% 35%",
    },
  },
  {
    id: "rose-soft",
    name: "Rose Soft",
    displayFont: "'Nunito Sans', system-ui, sans-serif",
    bodyFont: "'Nunito Sans', system-ui, sans-serif",
    isDark: false,
    colors: {
      background: "350 30% 98%",
      foreground: "350 20% 15%",
      card: "0 0% 100%",
      cardForeground: "350 20% 15%",
      primary: "350 70% 55%",
      primaryForeground: "350 30% 98%",
      muted: "350 20% 93%",
      mutedForeground: "350 10% 45%",
      border: "350 20% 88%",
      accent: "350 20% 93%",
      accentForeground: "350 20% 15%",
      menuBackground: "350 20% 15%",
      menuForeground: "350 30% 97%",
      menuActive: "350 60% 42%",
    },
  },
  {
    id: "charcoal-pro",
    name: "Charcoal Pro",
    displayFont: "'Manrope', system-ui, sans-serif",
    bodyFont: "'Manrope', system-ui, sans-serif",
    isDark: true,
    colors: {
      background: "240 6% 10%",
      foreground: "0 0% 93%",
      card: "240 5% 13%",
      cardForeground: "0 0% 93%",
      primary: "142 70% 50%",
      primaryForeground: "240 6% 10%",
      muted: "240 4% 18%",
      mutedForeground: "240 5% 60%",
      border: "240 4% 20%",
      accent: "240 4% 18%",
      accentForeground: "0 0% 93%",
      menuBackground: "240 6% 8%",
      menuForeground: "0 0% 90%",
      menuActive: "142 50% 30%",
    },
  },

  /* ---------- Temas neutros ---------- */
  {
    id: "paper-ink",
    name: "Papel & Tinta",
    displayFont: "'Inter', system-ui, sans-serif",
    bodyFont: "'Inter', system-ui, sans-serif",
    isDark: false,
    colors: {
      background: "40 12% 96%",
      foreground: "0 0% 12%",
      card: "0 0% 100%",
      cardForeground: "0 0% 12%",
      primary: "0 0% 16%",
      primaryForeground: "40 12% 97%",
      muted: "40 10% 91%",
      mutedForeground: "0 0% 42%",
      border: "40 10% 86%",
      accent: "40 10% 91%",
      accentForeground: "0 0% 12%",
      menuBackground: "0 0% 11%",
      menuForeground: "40 12% 96%",
      menuActive: "0 0% 25%",
    },
  },
  {
    id: "warm-sand",
    name: "Areia Quente",
    displayFont: "'DM Sans', system-ui, sans-serif",
    bodyFont: "'DM Sans', system-ui, sans-serif",
    isDark: false,
    colors: {
      background: "36 30% 97%",
      foreground: "28 18% 16%",
      card: "0 0% 100%",
      cardForeground: "28 18% 16%",
      primary: "28 28% 38%",
      primaryForeground: "36 30% 97%",
      muted: "36 22% 92%",
      mutedForeground: "28 12% 45%",
      border: "36 20% 87%",
      accent: "36 22% 92%",
      accentForeground: "28 18% 16%",
      menuBackground: "28 18% 15%",
      menuForeground: "36 30% 96%",
      menuActive: "28 28% 32%",
    },
  },
  {
    id: "stone-neutral",
    name: "Pedra Neutra",
    displayFont: "'Manrope', system-ui, sans-serif",
    bodyFont: "'Manrope', system-ui, sans-serif",
    isDark: false,
    colors: {
      background: "30 6% 97%",
      foreground: "24 8% 14%",
      card: "0 0% 100%",
      cardForeground: "24 8% 14%",
      primary: "24 6% 26%",
      primaryForeground: "30 6% 97%",
      muted: "30 5% 92%",
      mutedForeground: "24 5% 45%",
      border: "30 5% 88%",
      accent: "30 5% 92%",
      accentForeground: "24 8% 14%",
      menuBackground: "24 8% 13%",
      menuForeground: "30 6% 96%",
      menuActive: "24 6% 28%",
    },
  },
  {
    id: "greige-soft",
    name: "Greige Suave",
    displayFont: "'Outfit', system-ui, sans-serif",
    bodyFont: "'Outfit', system-ui, sans-serif",
    isDark: false,
    colors: {
      background: "40 8% 95%",
      foreground: "35 10% 18%",
      card: "40 14% 98%",
      cardForeground: "35 10% 18%",
      primary: "35 14% 40%",
      primaryForeground: "40 14% 98%",
      muted: "40 8% 90%",
      mutedForeground: "35 6% 46%",
      border: "40 8% 85%",
      accent: "40 8% 90%",
      accentForeground: "35 10% 18%",
      menuBackground: "35 12% 18%",
      menuForeground: "40 14% 96%",
      menuActive: "35 14% 34%",
    },
  },
  {
    id: "graphite-dark",
    name: "Grafite Neutro",
    displayFont: "'Inter', system-ui, sans-serif",
    bodyFont: "'Inter', system-ui, sans-serif",
    isDark: true,
    colors: {
      background: "220 8% 11%",
      foreground: "220 10% 94%",
      card: "220 8% 14%",
      cardForeground: "220 10% 94%",
      primary: "220 10% 82%",
      primaryForeground: "220 8% 12%",
      muted: "220 7% 19%",
      mutedForeground: "220 6% 62%",
      border: "220 7% 22%",
      accent: "220 7% 19%",
      accentForeground: "220 10% 94%",
      menuBackground: "220 9% 9%",
      menuForeground: "220 10% 92%",
      menuActive: "220 8% 26%",
    },
  },
  {
    id: "linen-clean",
    name: "Linho Clean",
    displayFont: "'Nunito Sans', system-ui, sans-serif",
    bodyFont: "'Nunito Sans', system-ui, sans-serif",
    isDark: false,
    colors: {
      background: "45 25% 97%",
      foreground: "45 12% 16%",
      card: "0 0% 100%",
      cardForeground: "45 12% 16%",
      primary: "20 25% 45%",
      primaryForeground: "45 25% 97%",
      muted: "45 18% 92%",
      mutedForeground: "45 8% 45%",
      border: "45 16% 87%",
      accent: "45 18% 92%",
      accentForeground: "45 12% 16%",
      menuBackground: "45 12% 14%",
      menuForeground: "45 25% 96%",
      menuActive: "20 25% 38%",
    },
  },
];


type AdminThemeSettings = {
  themeId: string;
  customColors?: Partial<AdminThemeColors> | null;
  customFonts?: { displayFont?: string; bodyFont?: string } | null;
};

interface AdminThemeContextType {
  currentTheme: AdminTheme;
  customColors: Partial<AdminThemeColors> | null;
  customFonts: { displayFont?: string; bodyFont?: string } | null;
  saveTheme: (settings: AdminThemeSettings) => Promise<void>;
  /** Aplica um tema temporariamente no admin inteiro (sem salvar). */
  previewTheme: (settings: AdminThemeSettings, durationMs?: number) => void;
  stopPreview: () => void;
  isPreviewing: boolean;
  isLoading: boolean;
}

const AdminThemeContext = createContext<AdminThemeContextType | undefined>(undefined);

const ADMIN_THEME_KEYS = [
  "--background",
  "--foreground",
  "--card",
  "--card-foreground",
  "--popover",
  "--popover-foreground",
  "--primary",
  "--primary-foreground",
  "--secondary",
  "--secondary-foreground",
  "--muted",
  "--muted-foreground",
  "--accent",
  "--accent-foreground",
  "--border",
  "--input",
  "--ring",
  "--font-display",
  "--font-body",
  "--admin-menu",
  "--admin-menu-foreground",
  "--admin-menu-active",
];

const ADMIN_THEME_CACHE_KEY = "admin-theme-settings-v2";

const readCachedSettings = (): AdminThemeSettings | null => {
  try {
    // Remove caches de versões antigas (podiam prender um tema escuro antigo).
    localStorage.removeItem("admin-theme-settings-v1");
    const raw = localStorage.getItem(ADMIN_THEME_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AdminThemeSettings & { savedAt?: number };
    // Keep the last known theme for startup, even after long periods offline.
    // The server refresh below still runs on every application launch.
    if (!adminThemes.some((theme) => theme.id === parsed?.themeId)) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeCachedSettings = (settings: AdminThemeSettings | null) => {
  try {
    if (!settings) localStorage.removeItem(ADMIN_THEME_CACHE_KEY);
    else localStorage.setItem(ADMIN_THEME_CACHE_KEY, JSON.stringify({ ...settings, savedAt: Date.now() }));
  } catch {
    /* ignore */
  }
};


export const AdminThemeProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const wasAdminRouteRef = useRef(false);

  const cached = useRef<AdminThemeSettings | null>(readCachedSettings()).current;

  const [currentTheme, setCurrentTheme] = useState<AdminTheme>(
    () => adminThemes.find((t) => t.id === cached?.themeId) ?? adminThemes[0],
  );
  const [customColors, setCustomColors] = useState<Partial<AdminThemeColors> | null>(
    cached?.customColors ?? null,
  );
  const [customFonts, setCustomFonts] = useState<{ displayFont?: string; bodyFont?: string } | null>(
    cached?.customFonts ?? null,
  );
  // When we have a cached theme we can paint immediately and reconcile in background,
  // avoiding the flash of default colors on page refresh.
  const [isLoading, setIsLoading] = useState(!cached);
  const [preview, setPreview] = useState<AdminThemeSettings | null>(null);
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);


  const stopPreview = () => {
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = null;
    setPreview(null);
  };

  const previewTheme = (settings: AdminThemeSettings, durationMs = 10000) => {
    if (previewTimer.current) clearTimeout(previewTimer.current);
    setPreview(settings);
    previewTimer.current = setTimeout(() => {
      previewTimer.current = null;
      setPreview(null);
    }, durationMs);
  };

  useEffect(() => () => {
    if (previewTimer.current) clearTimeout(previewTimer.current);
  }, []);

  const resolvedTheme = useMemo(() => {
    const baseTheme = preview
      ? adminThemes.find((t) => t.id === preview.themeId) ?? currentTheme
      : currentTheme;
    const overrideColors = preview ? preview.customColors : customColors;
    const overrideFonts = preview ? preview.customFonts : customFonts;
    const colors = { ...baseTheme.colors, ...(overrideColors ?? {}) };
    const displayFont = overrideFonts?.displayFont || baseTheme.displayFont;
    const bodyFont = overrideFonts?.bodyFont || baseTheme.bodyFont;
    return { ...baseTheme, colors, displayFont, bodyFont };
  }, [currentTheme, customColors, customFonts, preview]);


  // Lazy-load admin fonts only when entering admin routes
  useEffect(() => {
    if (!isAdminRoute) return;
    const ADMIN_FONTS_ID = "admin-theme-fonts";
    if (document.getElementById(ADMIN_FONTS_ID)) return;
    const link = document.createElement("link");
    link.id = ADMIN_FONTS_ID;
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=DM+Sans:wght@300;400;500;600;700&family=Plus+Jakarta+Sans:wght@300;400;500;600;700&family=Space+Grotesk:wght@300;400;500;600;700&family=Outfit:wght@300;400;500;600;700&family=Nunito+Sans:wght@300;400;500;600;700&family=Manrope:wght@300;400;500;600;700&family=Poppins:wght@300;400;500;600;700&family=Roboto:wght@300;400;500;600;700&family=Playfair+Display:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
  }, [isAdminRoute]);

  // Only fetch the admin theme settings when actually on /admin routes.
  // Avoids an unnecessary Supabase request on every public page load.
  const themeLoadedRef = useRef(false);
  useEffect(() => {
    if (!isAdminRoute) return;
    if (themeLoadedRef.current) return;
    themeLoadedRef.current = true;
    loadTheme();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminRoute]);

  // Sessões que ficam abertas por dias (PDV, celular, CEO) precisam pegar o
  // tema atual sem depender de recarregar a página.
  useEffect(() => {
    if (!isAdminRoute) return;
    const refresh = () => {
      if (document.visibilityState === "visible") void loadTheme();
    };
    const interval = setInterval(refresh, 5 * 60_000);
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminRoute]);


  // Apply before paint, including navigation back from public pages.
  useLayoutEffect(() => {

    
    if (isAdminRoute) {
      if (isLoading) return;
      applyTheme();
    } else if (wasAdminRouteRef.current) {
      // We just left /admin: remove admin overrides and re-apply the public site theme.
      clearTheme();
      applyStatusBarColor(null);
      window.dispatchEvent(new Event("site-theme:reapply"));
    }

    wasAdminRouteRef.current = isAdminRoute;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminRoute, resolvedTheme, isLoading]);

  const loadTheme = async () => {
    try {
      const { data, error } = await supabase
        .from("admin_settings")
        .select("value")
        .eq("key", "admin_theme")
        .maybeSingle();

      if (!error && data?.value) {
        const themeSettings = data.value as AdminThemeSettings;
        const theme = adminThemes.find((t) => t.id === themeSettings.themeId);
        if (theme) setCurrentTheme(theme);
        setCustomColors(themeSettings.customColors ?? null);
        setCustomFonts(themeSettings.customFonts ?? null);
        writeCachedSettings({
          themeId: themeSettings.themeId,
          customColors: themeSettings.customColors ?? null,
          customFonts: themeSettings.customFonts ?? null,
        });
      } else if (error) {
        // A transient failure must not replace the last known theme.
        console.warn("Admin theme unavailable; keeping current theme:", error.message);
      }


    } catch (error) {
      console.error("Error loading admin theme:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const clearTheme = () => {
    const root = document.documentElement;
    ADMIN_THEME_KEYS.forEach((k) => root.style.removeProperty(k));
    root.style.removeProperty("color-scheme");
  };

  /**
   * Barra de status do celular (Android/Chrome) segue o tema do admin.
   * Sem isso ela fica sempre com o marrom da loja (#c4856b).
   */
  const applyStatusBarColor = (hslValue: string | null) => {
    const meta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
    if (!meta) return;
    if (!hslValue) {
      meta.setAttribute("content", meta.dataset.siteColor || "#c4856b");
      return;
    }
    if (!meta.dataset.siteColor) meta.dataset.siteColor = meta.getAttribute("content") || "#c4856b";
    meta.setAttribute("content", `hsl(${hslValue})`);
  };

  const applyMenuColors = () => {
    const root = document.documentElement;
    const c = resolvedTheme.colors;
    root.style.setProperty("--admin-menu", c.menuBackground || DEFAULT_MENU_COLORS.menuBackground);
    root.style.setProperty(
      "--admin-menu-foreground",
      c.menuForeground || DEFAULT_MENU_COLORS.menuForeground,
    );
    root.style.setProperty("--admin-menu-active", c.menuActive || DEFAULT_MENU_COLORS.menuActive);
    applyStatusBarColor(c.primary || c.menuBackground || null);
  };

  /**
   * Guarda as variáveis CSS já resolvidas para que o bootstrap em index.html
   * pinte o painel (inclusive a tela de login) com o tema correto antes do React.
   */
  const persistThemeVars = () => {
    try {
      const root = document.documentElement;
      const vars: Record<string, string> = {};
      ADMIN_THEME_KEYS.forEach((k) => {
        const v = root.style.getPropertyValue(k);
        if (v) vars[k] = v;
      });
      const cs = root.style.getPropertyValue("color-scheme");
      if (cs) vars["color-scheme"] = cs;
      localStorage.setItem("admin-theme-vars-v1", JSON.stringify(vars));
    } catch {
      /* ignore */
    }
  };

  const applyTheme = () => {
    const root = document.documentElement;
    applyMenuColors();

    // Theme that matches the current admin/site defaults: don't override anything.
    const activeCustomColors = preview ? preview.customColors : customColors;
    const activeCustomFonts = preview ? preview.customFonts : customFonts;
    if (resolvedTheme.id === "site-default" && !activeCustomColors && !activeCustomFonts) {
      clearTheme();
      applyMenuColors();
      if (!preview) persistThemeVars();
      return;
    }


    const c = resolvedTheme.colors;

    root.style.setProperty("--background", c.background);
    root.style.setProperty("--foreground", c.foreground);

    root.style.setProperty("--card", c.card);
    root.style.setProperty("--card-foreground", c.cardForeground);

    root.style.setProperty("--popover", c.card);
    root.style.setProperty("--popover-foreground", c.cardForeground);

    root.style.setProperty("--primary", c.primary);
    root.style.setProperty("--primary-foreground", c.primaryForeground);

    root.style.setProperty("--secondary", c.muted);
    root.style.setProperty("--secondary-foreground", c.foreground);

    root.style.setProperty("--muted", c.muted);
    root.style.setProperty("--muted-foreground", c.mutedForeground);

    root.style.setProperty("--accent", c.accent);
    root.style.setProperty("--accent-foreground", c.accentForeground);

    root.style.setProperty("--border", c.border);
    root.style.setProperty("--input", c.border);
    root.style.setProperty("--ring", c.primary);

    // Fonts
    if (resolvedTheme.displayFont !== "var(--font-display)") {
      root.style.setProperty("--font-display", resolvedTheme.displayFont);
    } else {
      root.style.removeProperty("--font-display");
    }

    if (resolvedTheme.bodyFont !== "var(--font-body)") {
      root.style.setProperty("--font-body", resolvedTheme.bodyFont);
    } else {
      root.style.removeProperty("--font-body");
    }

    root.style.setProperty("color-scheme", resolvedTheme.isDark ? "dark" : "light");
    if (!preview) persistThemeVars();
  };


  const saveTheme = async (settings: AdminThemeSettings) => {
    const theme = adminThemes.find((t) => t.id === settings.themeId);
    if (!theme) return;
    stopPreview();


    setCurrentTheme(theme);
    setCustomColors(settings.customColors ?? null);
    setCustomFonts(settings.customFonts ?? null);
    writeCachedSettings({
      themeId: settings.themeId,
      customColors: settings.customColors ?? null,
      customFonts: settings.customFonts ?? null,
    });


    try {
      const { error } = await supabase
        .from("admin_settings")
        .upsert(
          {
            key: "admin_theme",
            value: {
              themeId: settings.themeId,
              customColors: settings.customColors ?? null,
              customFonts: settings.customFonts ?? null,
            },
            updated_at: new Date().toISOString(),
          },
          { onConflict: "key" }
        );

      if (error) throw error;
    } catch (error) {
      console.error("Error saving admin theme:", error);
      throw error;
    }
  };

  return (
    <AdminThemeContext.Provider
      value={{
        currentTheme,
        customColors,
        customFonts,
        saveTheme,
        previewTheme,
        stopPreview,
        isPreviewing: preview !== null,
        isLoading,
      }}
    >
      {isAdminRoute && isLoading ? (
        <div
          role="status"
          aria-live="polite"
          style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "#f8fafc", color: "#475569" }}
        >
          Carregando painel…
        </div>
      ) : children}
    </AdminThemeContext.Provider>
  );
};

export const useAdminTheme = () => {
  const context = useContext(AdminThemeContext);
  if (context === undefined) {
    throw new Error("useAdminTheme must be used within an AdminThemeProvider");
  }
  return context;
};
